import type { ThreatReportSummary } from '@/shared/cors-edge-lambda-api';

/** A report on the case, from the caller's list, the shared list, or both. */
export interface CaseThreatReport extends ThreatReportSummary {
  /** The signed-in analyst generated this version. */
  isOwner: boolean;
}

export function threatReportIdentity(report: Pick<ThreatReportSummary, 'chatKey' | 'epochTimeSecs'>): string {
  return `${report.chatKey}:${report.epochTimeSecs}`;
}

function sameAnalyst(createdBy: string | null, sessionEmail: string): boolean {
  const owner = createdBy?.trim().toLowerCase() ?? '';
  const session = sessionEmail.trim().toLowerCase();
  return owner !== '' && owner === session;
}

/**
 * Merge the caller's reports with the case's shared reports.
 *
 * The caller's row wins when both lists have the same version. A caller's
 * report is also shared when it appears on the shared list, which covers the
 * window before the caller's own summary includes the flag.
 */
export function mergeCaseThreatReports(
  own: ThreatReportSummary[],
  shared: ThreatReportSummary[],
  sessionEmail: string,
): CaseThreatReport[] {
  const sharedIds = new Set(shared.map(threatReportIdentity));
  const byId = new Map<string, CaseThreatReport>();

  for (const report of shared) {
    byId.set(threatReportIdentity(report), {
      ...report,
      shared: true,
      isOwner: sameAnalyst(report.createdBy, sessionEmail),
    });
  }

  for (const report of own) {
    const id = threatReportIdentity(report);
    byId.set(id, {
      ...report,
      shared: report.shared || sharedIds.has(id),
      isOwner: true,
    });
  }

  return [...byId.values()].sort((a, b) => b.epochTimeSecs - a.epochTimeSecs);
}
